import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import SMCPage from '../../components/core/pageComponents/SMCPage';
import {SMCTabsContainer, SMCTab} from '../../components/core/SMCTabs';
import {useTranslation} from 'react-i18next';
import Api from '../../api/SMPFetchHelper';
import {SessionDataGetSchema} from '../../api/DBSchema/SessionData';
import OverviewTab from './OverviewTab';
import SessionTab from './SessionTab';
import {ApiErrorCodes, ObjectTypes} from '../../api/consts';
import {getErrorCode} from '../../utils/utils';
import {push} from 'connected-react-router';
import {SMCPaths, helpContextPageUrl} from '../../conf/Routes';

function loadAllData() {
    return new Promise(async (resolve, reject) => {
        try {
            const [gatewaysStatusResp, sessionDataResp, getSessionsResp, getPlanCountResp, getOverviewNoticeMessageResp] = await Promise.allSettled([
                Api.getGatewaysStatus(),
                Api.getSessionData({
                    attributes: [SessionDataGetSchema.serviceCenterName]
                }),
                Api.getSessions(),
                Api.getObjectsCount(ObjectTypes.PLAN),
                Api.getOverviewNoticeMessage()
            ]);

            // Reject only if error code is invalid session (only need to check one, they will all return this)
            if (gatewaysStatusResp.status === 'rejected' && getErrorCode(gatewaysStatusResp.reason) === ApiErrorCodes.INVALID_SESSION_ERROR) {
                reject(gatewaysStatusResp.reason);
            }

            return resolve({
                connected: gatewaysStatusResp.status === 'fulfilled' ? gatewaysStatusResp.value.data.connected : null,
                disconnected: gatewaysStatusResp.status === 'fulfilled' ? gatewaysStatusResp.value.data.disconnected : null,
                disabled: gatewaysStatusResp.status === 'fulfilled' ? gatewaysStatusResp.value.data.disabled : null,
                serviceCenterName: sessionDataResp.status === 'fulfilled' ? sessionDataResp.value.data[SessionDataGetSchema.serviceCenterName] : null,
                plansCount: getPlanCountResp.status === 'fulfilled' ? getPlanCountResp.value.data.data.count : null,
                loggedIn: getSessionsResp.status === 'fulfilled' ? getSessionsResp.value.data.data.length : null,
                overviewNoticeMessage: getOverviewNoticeMessageResp.status === 'fulfilled' ? getOverviewNoticeMessageResp.value.data.overviewNoticeMessage : null
            });
        } catch (e) {
            return reject(e);
        }
    })
}

function Home({
    navigateTo
}) {
    const { t } = useTranslation();

    const STATUS_HELP_ID   = helpContextPageUrl.smpStatus;     
    const SESSIONS_HELP_ID   = helpContextPageUrl.userSessions;        

    const [connectedGateways, setConnectedGateways] = useState(null);
    const [disconnectedGateways, setDisconnectedGateways] = useState(null);
    const [disabledGateways, setDisabledGateways] = useState(null);
    const [serviceCenterName, setServiceCenterName] = useState(null);
    const [plansCount, setPlansCount] = useState(null);
    const [loggedInUsersCount, setLoggedInUsersCount] = useState(null);
    const [overviewNoticeMessage, setOverviewNoticeMessage] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    
    const [currentlySelectedTab, setCurrentlySelectedTab] = useState('STATUS');
    const helpId = currentlySelectedTab === 'STATUS' ? STATUS_HELP_ID : SESSIONS_HELP_ID;

    // Load all data on first load
    useEffect(() => {
        let isSubscribed = true;
        loadAllData().then(data => {
            if (isSubscribed) {
                setConnectedGateways(data.connected);
                setDisconnectedGateways(data.disconnected);
                setDisabledGateways(data.disabled);
                setServiceCenterName(data.serviceCenterName);
                setPlansCount(data.plansCount);
                setLoggedInUsersCount(data.loggedIn);
                setOverviewNoticeMessage(data.overviewNoticeMessage);
                setDataLoaded(true);
            }
        }).catch(e => {
            if (isSubscribed) {
                const errorCode = getErrorCode(e);
                if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                    navigateTo(SMCPaths.logout);
                }
            }
        })

        return () => { isSubscribed = false; }
    }, [navigateTo]);

    const reloadAllData = useCallback(() => {
        setDataLoaded(false);
        loadAllData().then(data => {
            setConnectedGateways(data.connected);
            setDisconnectedGateways(data.disconnected);
            setDisabledGateways(data.disabled);
            setPlansCount(data.plansCount);
            setLoggedInUsersCount(data.loggedIn);
            setOverviewNoticeMessage(data.overviewNoticeMessage);
        })
        .finally(() => setDataLoaded(true));
    }, []);

    return (
        <SMCPage loading={!dataLoaded} helpId={helpId}>
            <SMCTabsContainer minHeight={450} width={552} tabOnClick={setCurrentlySelectedTab}>
                <SMCTab title={t("SMC_WELCOME_TAB0")} id="STATUS">
                    <OverviewTab
                        connectedGateways={connectedGateways}
                        disabledGateways={disabledGateways}
                        disconnectedGateways={disconnectedGateways}
                        loggedInUsersCount={loggedInUsersCount}
                        plansCount={plansCount}
                        serviceCenterName={serviceCenterName}
                        reload={reloadAllData}
                        overviewNoticeMessage={overviewNoticeMessage}
                    />
                </SMCTab>
                <SMCTab title={t("SMC_WELCOME_TAB1")} id="SESSIONS">
                    <SessionTab />
                </SMCTab>
            </SMCTabsContainer>
        </SMCPage>
    );
}

const mapStateToProps = null;

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(Home);